import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FAQ_DISPOSITION_NOTE_MAX_LENGTH } from '@cortex/domain-ac-mens-hub-api';
import { useSetDisposition } from '../../hooks/useFaqMonitor.js';
import type { FaqDisposition, FaqDispositionStatus } from '../../types/faq-monitor.js';

/**
 * 対応方針の選択肢。ラベルは i18n キーで引く。
 * 一覧で現在値だけを出す側（ALF実績の表など）も同じラベルを使うため公開する。
 * @graph-connects none
 */
export const DISPOSITION_OPTIONS: readonly { value: FaqDispositionStatus; labelKey: string }[] = [
  { value: 'open', labelKey: 'faqMonitor.dispositionOpen' },
  { value: 'will_fix', labelKey: 'faqMonitor.dispositionWillFix' },
  { value: 'wont_fix', labelKey: 'faqMonitor.dispositionWontFix' },
  { value: 'spec_wrong', labelKey: 'faqMonitor.dispositionSpecWrong' },
  { value: 'done', labelKey: 'faqMonitor.dispositionDone' },
];

/**
 * 対応方針にメモを残す欄。
 *
 * メモは「なぜその方針にしたか」を残すためのもの。方針の区分だけでは
 * 「別案件で改修中」のような状況が伝わらず、次に見た人が同じ調査をやり直す。
 *
 * 入力中は毎打鍵で保存しない。フォーカスが外れた時点で、値が変わっていれば保存する。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 指摘・論点に対する対応方針とメモを記録できるようにする
 * @graph-connects useSetDisposition [calls] 対応方針の保存
 */
export function DispositionEditor({
  hash,
  disposition,
}: {
  hash: string;
  disposition: FaqDisposition;
}) {
  const { t } = useTranslation();
  const setDisposition = useSetDisposition();
  const [draftNote, setDraftNote] = useState(disposition.note);
  // 直前に受け取ったサーバー側の値。draftNote が「未編集」かどうかの判定基準にする。
  const syncedNoteRef = useRef(disposition.note);

  // 他の人が保存すると invalidateQueries でワークリストが再取得されるが、
  // 行は key で同一視されるため DispositionEditor は再マウントされない。
  // draftNote を追従させないと、未編集のまま blur しただけで
  // draftNote(古い値) !== disposition.note(新しい値) が成立し、他人のメモを黙って上書きする。
  // 編集途中の入力は消さない（ローカルに差分があるときは追従しない）。
  if (syncedNoteRef.current !== disposition.note) {
    const hasLocalEdits = draftNote !== syncedNoteRef.current;
    syncedNoteRef.current = disposition.note;
    if (!hasLocalEdits) setDraftNote(disposition.note);
  }

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-md border border-border bg-background-secondary px-3 py-2">
      <label className="flex items-center gap-2 text-xs">
        <span className="text-foreground-muted">{t('faqMonitor.dispositionLabel')}</span>
        <select
          value={disposition.status}
          disabled={setDisposition.isPending}
          onChange={(e) => {
            setDisposition.mutate({
              hash,
              input: { status: e.target.value as FaqDispositionStatus, note: draftNote },
            });
          }}
          className="rounded border border-border bg-background px-2 py-1 text-foreground"
        >
          {DISPOSITION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>
      </label>
      <textarea
        value={draftNote}
        rows={2}
        maxLength={FAQ_DISPOSITION_NOTE_MAX_LENGTH}
        placeholder={t('faqMonitor.notePlaceholder')}
        aria-label={t('faqMonitor.noteLabel')}
        disabled={setDisposition.isPending}
        onChange={(e) => {
          setDraftNote(e.target.value);
        }}
        onBlur={() => {
          // 変わっていなければ保存しない。開いて閉じただけで更新者が書き換わるのを防ぐ。
          if (draftNote === disposition.note) return;
          setDisposition.mutate({ hash, input: { status: disposition.status, note: draftNote } });
        }}
        className="rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
      />
      {disposition.updatedBy !== '' && (
        <p className="text-xs text-foreground-muted">
          {t('faqMonitor.dispositionUpdatedBy', {
            who: disposition.updatedBy,
            date: disposition.updatedAt.slice(0, 10),
          })}
        </p>
      )}
    </div>
  );
}

/**
 * 対応方針の表示と、メモ欄の開閉。
 *
 * 既定では畳む。全件に空の入力欄を開いておくと、実際に書くのは数件なのに
 * 空欄が画面の半分を占める。方針が付いている件とメモがある件だけ最初から開く。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 対応方針の現在値を1行で示し、必要なときだけメモ欄を開けるようにする
 * @graph-connects DispositionEditor [calls] 対応方針とメモの編集
 */
export function DispositionControl({
  hash,
  disposition,
}: {
  hash: string;
  disposition: FaqDisposition;
}) {
  const { t } = useTranslation();
  const decided = disposition.status !== 'open' || disposition.note !== '';
  const [open, setOpen] = useState(decided);
  const statusLabel = DISPOSITION_OPTIONS.find((o) => o.value === disposition.status)?.labelKey;

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-end gap-2 text-xs">
        <span className="text-foreground-secondary">
          {statusLabel === undefined ? disposition.status : t(statusLabel)}
        </span>
        <button
          type="button"
          onClick={() => {
            setOpen((prev) => !prev);
          }}
          aria-expanded={open}
          className="text-blue-600 underline dark:text-blue-400"
        >
          {open ? t('faqMonitor.closeMemo') : t('faqMonitor.openMemo')}
        </button>
      </div>
      {open && <DispositionEditor hash={hash} disposition={disposition} />}
    </div>
  );
}
